// whats-new 1.3.0, copied from ~/Projects/whats-new by its install.sh. Change it there and reinstall; edits here are overwritten.
// What's new: the logic behind an app's "What's new" pop-up and its full history in Settings.
//
// The app keeps a changelog.json, newest release first, written for the people using it. On each
// load it asks `whatsNew()` whether there is anything to announce since the version this device last
// showed; it draws the pop-up itself, in its own design, and calls `markSeen()` when it is dismissed.
// A first visit has nothing to announce: the version is recorded quietly.
//
// No imports and no DOM types, so the same source runs in a browser page, a bundled app and Node.

export interface Entry {
  /** Semantic version, "1.4.0". */
  version: string
  /** Release date, "2026-10-09". */
  date: string
  /**
   * What changed, one short sentence each, in the words of the people using the app: what the pop-up and the
   * history show. Empty when everything in the release is `hidden`.
   */
  items: string[]
  /** What changed but isn't announced: kept here for the record, never shown in the pop-up or the history. */
  hidden?: string[]
}

/** Anything with localStorage's get and set, sync or async: localStorage itself, chrome.storage wrappers, a test map. */
export interface SeenStore {
  get(key: string): string | null | undefined | Promise<string | null | undefined>
  set(key: string, value: string): void | Promise<void>
}

export interface Announcement {
  /** The version last shown on this device, or null when it had none on record. */
  from: string | null
  /** The version running now. */
  to: string
  /** The releases to show, newest first, at most `max`. */
  entries: Entry[]
  /** How many more releases there were beyond `entries`, for "…and 3 earlier updates". */
  more: number
  /** Records `to` as seen; call it when the pop-up is dismissed. */
  markSeen(): Promise<void>
}

export interface WhatsNewOptions {
  changelog: Entry[]
  /** The version running now. Defaults to the changelog's newest. */
  current?: string
  /** Where the last-shown version is kept. Defaults to localStorage, or nowhere when there is none. */
  store?: SeenStore
  /** The storage key. Default "whatsNew.lastSeen". */
  key?: string
  /** The most releases to put in the pop-up. Default 6. */
  max?: number
  /**
   * When there is no version on record: false (the default) records the current one quietly, since a
   * new user has nothing to compare with; true announces just the current release, for an app that
   * has been in use since before it had a changelog.
   */
  announceWithoutHistory?: boolean
}

const VERSION = /^(\d+)\.(\d+)\.(\d+)$/
const DATE = /^\d{4}-\d{2}-\d{2}$/

/** -1, 0 or 1 as `a` is older than, the same as or newer than `b`. Versions are "major.minor.patch". */
export function compareVersions(a: string, b: string): number {
  const pa = VERSION.exec(a)
  const pb = VERSION.exec(b)
  if (!pa || !pb) throw new Error(`Not a version: ${!pa ? a : b}`)
  for (let i = 1; i <= 3; i++) {
    const d = Number(pa[i]) - Number(pb[i])
    if (d !== 0) return d < 0 ? -1 : 1
  }
  return 0
}

/** Problems with a changelog, as sentences; empty when it is fine. */
export function validate(changelog: unknown): string[] {
  if (!Array.isArray(changelog)) return ['The changelog must be a list of releases.']
  const problems: string[] = []
  const seen = new Set<string>()
  changelog.forEach((e: unknown, i) => {
    const at = `Release ${i + 1}`
    if (!e || typeof e !== 'object') return void problems.push(`${at} is not an object.`)
    const { version, date, items, hidden } = e as Partial<Entry>
    if (typeof version !== 'string' || !VERSION.test(version)) problems.push(`${at} has no version like "1.4.0".`)
    else if (seen.has(version)) problems.push(`${version} is listed twice.`)
    else seen.add(version)
    if (typeof date !== 'string' || !DATE.test(date)) problems.push(`${at} (${version ?? '?'}) has no date like "2026-10-09".`)
    const sentences = (list: unknown) => Array.isArray(list) && list.every((s) => typeof s === 'string' && s.trim())
    if (!sentences(items) || (hidden !== undefined && !sentences(hidden)) || (items as string[]).length + (hidden?.length ?? 0) === 0) {
      problems.push(`${at} (${version ?? '?'}) needs at least one item (or hidden item), each a non-empty sentence.`)
    }
  })
  // newest first, so the pop-up and the history read top-down
  for (let i = 1; i < changelog.length; i++) {
    const a = (changelog[i - 1] as Partial<Entry>)?.version
    const b = (changelog[i] as Partial<Entry>)?.version
    if (typeof a === 'string' && typeof b === 'string' && VERSION.test(a) && VERSION.test(b) && compareVersions(a, b) <= 0) {
      problems.push(`${a} is listed before ${b}; put the newest release first.`)
    }
  }
  return problems
}

/** The entry for a version, if the changelog has one. */
export function entryFor(changelog: Entry[], version: string): Entry | undefined {
  return changelog.find((e) => e.version === version)
}

/** The newest version in the changelog, or null when it is empty. */
export function latest(changelog: Entry[]): string | null {
  return changelog[0]?.version ?? null
}

/** The releases with something to show, for the history: a release that is all `hidden` has nothing to say. */
export function listed(changelog: Entry[]): Entry[] {
  return changelog.filter((e) => e.items.length > 0)
}

/**
 * The releases after `from` up to and including `to`, newest first. With no `from`, just `to`'s own.
 * A release newer than `to` is never included: a device can run an older build than the changelog.
 */
export function entriesBetween(changelog: Entry[], from: string | null, to: string): Entry[] {
  return changelog.filter((e) => compareVersions(e.version, to) <= 0 && (from ? compareVersions(e.version, from) > 0 : e.version === to))
}

function defaultStore(): SeenStore | undefined {
  const ls = (globalThis as { localStorage?: { getItem(k: string): string | null; setItem(k: string, v: string): void } }).localStorage
  if (!ls) return undefined
  // private windows and blocked storage throw; the worst case is the pop-up showing again
  return {
    get: (k) => {
      try {
        return ls.getItem(k)
      } catch {
        return null
      }
    },
    set: (k, v) => {
      try {
        ls.setItem(k, v)
      } catch {
        // nothing to do
      }
    },
  }
}

/**
 * What to announce on this load, or null. Null also records the current version when that is all
 * there is to do (a first visit, or nothing listed in the changelog since last time), so it is safe to call
 * on every load.
 */
export async function whatsNew(options: WhatsNewOptions): Promise<Announcement | null> {
  const { changelog, key = 'whatsNew.lastSeen', max = 6, announceWithoutHistory = false } = options
  const to = options.current ?? latest(changelog)
  if (!to || !VERSION.test(to)) return null
  const store = options.store ?? defaultStore()
  const markSeen = async () => {
    await store?.set(key, to)
  }
  const raw = (await store?.get(key)) ?? null
  const seen = raw && VERSION.test(raw) ? raw : null
  if (seen === to) return null
  // a first visit, or a device that went back to an older build: nothing to say
  if ((!seen && !announceWithoutHistory) || (seen && compareVersions(seen, to) > 0)) {
    await markSeen()
    return null
  }
  // releases that are all `hidden` are passed over, so one on its own announces nothing
  const all = listed(entriesBetween(changelog, seen, to))
  if (all.length === 0) {
    await markSeen()
    return null
  }
  return { from: seen, to, entries: all.slice(0, max), more: Math.max(0, all.length - max), markSeen }
}
